import React from 'react';
import { useNavigate } from 'react-router-dom';

import { isoToTimeHHMM } from '../../domain/events/iso';
import { canCreateEvent } from '../../domain/events/policy';
import { nearestRuleOnDate, openResourceIdsOnDate } from '../../domain/guestAccess/guestAccess';
import type { CalendarMode } from '../../features/floorPlan/calendar';
import { CalendarPicker } from '../../features/floorPlan/CalendarPicker';
import { FloorPlanLegend } from '../../features/floorPlan/FloorPlanLegend';
import { FloorPlanMap } from '../../features/floorPlan/FloorPlanMap';
import { MatchingPanel } from '../../features/matching/MatchingPanel';
import { useActiveGuestAccess } from '../../hooks/useActiveGuestAccess';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { uiActions } from '../../store/slices/uiSlice';
import { fetchEventsDate } from '../../store/thunks';
import { TavernButton, TavernLoading } from '../../ui/components';
import { formatDateYYYYMMDD } from '../../utils/date';

import './FloorPlanPage.css';

export function FloorPlanPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const events = useAppSelector((s) => s.entities.events.items);
  const role = useAppSelector((s) => s.auth.user?.role);

  const [mode, setMode] = React.useState<CalendarMode>('week');
  const [isLoading, setIsLoading] = React.useState(false);

  const dateKey = useAppSelector((s) => s.ui.selectedDate);
  const selectedDate = React.useMemo(() => new Date(dateKey + 'T00:00:00'), [dateKey]);

  const setSelectedDate = React.useCallback(
    (date: Date) => {
      dispatch(uiActions.setSelectedDate(formatDateYYYYMMDD(date)));
    },
    [dispatch]
  );

  const { rules: guestRules } = useActiveGuestAccess();
  const guestOpenResourceIds = React.useMemo(() => openResourceIdsOnDate(guestRules, dateKey), [dateKey, guestRules]);
  const guestRule = React.useMemo(() => nearestRuleOnDate(guestRules, dateKey), [dateKey, guestRules]);
  const canCreate =canCreateEvent(role, guestOpenResourceIds.size > 0);

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchEventsDate({ date: dateKey })).unwrap();
      } catch (error) {
        // Errors are logged by API client and handled by Redux state
        // Swallow them here to prevent unhandled rejections in React Strict Mode
        console.debug('FloorPlanPage: fetchEventsDate failed, ignoring', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [dateKey, dispatch]);

  return (
    <div className="ttbPage floor-plan-page">
      <div className="floor-plan-header">
        <h1 className="ttbPageTitle">
          <span>🗺️</span>
          План таверны
        </h1>
        {canCreate ? (
          <TavernButton
            variant="primary"
            className="floor-plan-create-button"
            onClick={() => navigate(`/events/new?date=${encodeURIComponent(dateKey)}`)}
          >
            ⚔️ Создать
          </TavernButton>
        ) : null}
      </div>

      <div className="ttbPageContent floor-plan-content">
        <MatchingPanel />

        <CalendarPicker
          mode={mode}
          selectedDate={selectedDate}
          onChangeSelectedDate={setSelectedDate}
          onToggleMode={() => setMode((m) => (m === 'week' ? 'month' : 'week'))}
          events={events}
        />

        <div className="floor-plan-map-container">
          {guestRule ? (
            <div className="floor-plan-guest-notice">
              Гости могут бронировать столы с {isoToTimeHHMM(guestRule.startAt)} и по {isoToTimeHHMM(guestRule.endAt)}
            </div>
          ) : null}
          {isLoading ? <TavernLoading /> : null}
          <FloorPlanMap selectedDate={selectedDate} events={events} guestOpenResourceIds={guestOpenResourceIds} />
          <FloorPlanLegend />
        </div>
      </div>
    </div>
  );
}
